import {
  BasicComponentType,
  LayoutChild,
  SectionComponentType,
} from "@/types/property-panel.types";
import {
  GENERATED_ID_PREFIX,
  LAYOUT_ROW_PREFIX,
  isGeneratedId,
  isLayoutChild,
  isSectionChild,
  makeLayoutChildId,
} from "./generated-id";
import {
  AlignHorizontalJustifyCenter,
  Award,
  Columns2,
  Columns3,
  Image,
  LayoutGrid,
  LayoutPanelTop,
  Mail,
  Megaphone,
  Menu,
  MessageSquareQuote,
  MousePointerClick,
  PanelLeft,
  PanelRight,
  PanelTop,
  PanelTopClose,
  Pill,
  Rows2,
  Rows3,
  SplitSquareHorizontal,
  Star,
  Type,
  Video,
  Music,
  Grid3X3,
  CreditCard,
  Layers,
  GalleryHorizontalEnd,
  MonitorSmartphone,
  ChevronDown,
  Search,
  CircleUser,
} from "lucide-react";

// ─── Section template color tokens ──────────────────────────────────
// Change these to update the palette across ALL section/nav templates.
const ST = {
  // Backgrounds
  bgLight: "bg-white dark:bg-gray-950",
  bgMuted: "bg-gray-50 dark:bg-gray-900",
  bgCard: "bg-white dark:bg-gray-800/60",
  bgDark: "bg-gray-950 dark:bg-black",
  bgPlaceholder: "bg-gray-100 dark:bg-gray-800",
  // Text
  heading: "text-gray-900 dark:text-white",
  body: "text-gray-500 dark:text-gray-400",
  muted: "text-gray-400 dark:text-gray-500",
  label: "text-gray-500 dark:text-gray-400",
  // Buttons
  btnPrimary:
    "bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100",
  btnSecondary:
    "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600",
  btnOutline:
    "ring-1 ring-gray-200 dark:ring-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700",
  // Borders & rings
  ring: "ring-1 ring-gray-100 dark:ring-gray-800",
  ringHover: "hover:ring-gray-300 dark:hover:ring-gray-600",
  border: "border-gray-100 dark:border-gray-800/60",
  // Scroll offset for fixed navs (scroll-margin-top: 5rem)
  scrollOffset: "scroll-mt-20",
} as const;

// Smooth scroll + :target highlight CSS injected into nav templates.
// When a user clicks an anchor link, the page scrolls smoothly and the
// target section flashes a subtle indigo ring to draw attention.
const NAV_SCROLL_STYLE = `<style>{"html{scroll-behavior:smooth}section:target,footer:target{animation:__optidev_hl 1.2s ease-out}@keyframes __optidev_hl{0%{box-shadow:inset 0 0 0 3px rgba(99,102,241,.4)}to{box-shadow:inset 0 0 0 3px transparent}}"}</style>`;

// Helper to strip unit and return just the number as string
function stripUnit(value: string | undefined | null): string {
  if (!value) return "0";
  if (typeof value === "number") return value + "";
  // Remove px, %, em, rem, etc.
  return value.replace(/[a-z%]+$/i, "");
}

// Helper to strip px and return just the number as string
function stripPx(value: string | undefined | null): string {
  if (!value) return "0";
  if (typeof value === "number") return value + "";
  return value.endsWith("px") ? value.slice(0, -2) : value;
}

// Helper to add px if not present
function addPx(value: string | number): string {
  if (typeof value === "number") return `${value}px`;
  if (value.trim() === "") return "0px";
  return value.endsWith("px") ? value : `${value}px`;
}

// Helper to add unit if not present
function addUnit(value: string | number, unit: string): string {
  if (typeof value === "number") return `${value}${unit}`;
  if (value.trim() === "") return `0${unit}`;
  return value.endsWith(unit) ? value : `${value}${unit}`;
}

// Helper to parse string to number or undefined
const parseNumber = (str: string | undefined | null): number | undefined => {
  if (str === undefined || str === null || str.trim() === "") return undefined;
  const num = parseFloat(str);
  return isNaN(num) ? undefined : num;
};

// CSS Properties options
const cssPropertyOptions = [
  // Typography
  { category: "Typography", value: "color", label: "Color" },
  { category: "Typography", value: "fontSize", label: "Font Size" },
  { category: "Typography", value: "fontFamily", label: "Font Family" },
  { category: "Typography", value: "fontWeight", label: "Font Weight" },
  { category: "Typography", value: "fontStyle", label: "Font Style" },
  { category: "Typography", value: "lineHeight", label: "Line Height" },
  { category: "Typography", value: "letterSpacing", label: "Letter Spacing" },
  { category: "Typography", value: "textAlign", label: "Text Align" },
  { category: "Typography", value: "textDecoration", label: "Text Decoration" },
  { category: "Typography", value: "textTransform", label: "Text Transform" },
  { category: "Typography", value: "textShadow", label: "Text Shadow" },

  // Background
  {
    category: "Background",
    value: "backgroundColor",
    label: "Background Color",
  },
  {
    category: "Background",
    value: "backgroundImage",
    label: "Background Image",
  },
  { category: "Background", value: "backgroundSize", label: "Background Size" },
  {
    category: "Background",
    value: "backgroundPosition",
    label: "Background Position",
  },
  {
    category: "Background",
    value: "backgroundRepeat",
    label: "Background Repeat",
  },

  // Border
  { category: "Border", value: "border", label: "Border" },
  { category: "Border", value: "borderColor", label: "Border Color" },
  { category: "Border", value: "borderWidth", label: "Border Width" },
  { category: "Border", value: "borderStyle", label: "Border Style" },
  { category: "Border", value: "borderRadius", label: "Border Radius" },

  // Layout
  { category: "Layout", value: "display", label: "Display" },
  { category: "Layout", value: "position", label: "Position" },
  { category: "Layout", value: "top", label: "Top" },
  { category: "Layout", value: "right", label: "Right" },
  { category: "Layout", value: "bottom", label: "Bottom" },
  { category: "Layout", value: "left", label: "Left" },
  { category: "Layout", value: "zIndex", label: "Z-Index" },

  // Flexbox
  { category: "Flexbox", value: "flexDirection", label: "Flex Direction" },
  { category: "Flexbox", value: "justifyContent", label: "Justify Content" },
  { category: "Flexbox", value: "alignItems", label: "Align Items" },
  { category: "Flexbox", value: "flexWrap", label: "Flex Wrap" },
  { category: "Flexbox", value: "gap", label: "Gap" },

  // Effects
  { category: "Effects", value: "opacity", label: "Opacity" },
  { category: "Effects", value: "boxShadow", label: "Box Shadow" },
  { category: "Effects", value: "transform", label: "Transform" },
  { category: "Effects", value: "transition", label: "Transition" },
  { category: "Effects", value: "filter", label: "Filter" },
];

// Helper functions for shadow parsing
const parseTextShadow = (shadowStr: string | undefined) => {
  if (!shadowStr || shadowStr === "none") {
    return { color: "rgb(0, 0, 0)", x: 0, y: 0, blur: 0 };
  }
  const colorMatch = shadowStr.match(/rgb\([^)]+\)/);
  const pxMatches = shadowStr.match(/(-?\d+(?:\.\d+)?)px/g) || [];

  return {
    color: colorMatch?.[0] || "rgb(0, 0, 0)",
    x: parseFloat(pxMatches[0]?.replace("px", "") || "0"),
    y: parseFloat(pxMatches[1]?.replace("px", "") || "0"),
    blur: parseFloat(pxMatches[2]?.replace("px", "") || "0"),
  };
};

const parseBoxShadow = (shadowStr: string | undefined) => {
  if (!shadowStr || shadowStr === "none") {
    return { color: "rgb(0, 0, 0)", x: 0, y: 0, blur: 0, spread: 0 };
  }
  const colorMatch = shadowStr.match(/rgb\([^)]+\)/);
  const pxMatches = shadowStr.match(/(-?\d+(?:\.\d+)?)px/g) || [];

  return {
    color: colorMatch?.[0] || "rgb(0, 0, 0)",
    x: parseFloat(pxMatches[0]?.replace("px", "") || "0"),
    y: parseFloat(pxMatches[1]?.replace("px", "") || "0"),
    blur: parseFloat(pxMatches[2]?.replace("px", "") || "0"),
    spread: parseFloat(pxMatches[3]?.replace("px", "") || "0"),
  };
};

const formatTextShadow = (shadow: {
  color: string;
  x: number;
  y: number;
  blur: number;
}) => {
  return `${shadow.color} ${shadow.x}px ${shadow.y}px ${shadow.blur}px`;
};

const formatBoxShadow = (shadow: {
  color: string;
  x: number;
  y: number;
  blur: number;
  spread: number;
}) => {
  return `${shadow.color} ${shadow.x}px ${shadow.y}px ${shadow.blur}px ${shadow.spread}px`;
};

const propertiesWithPx = [
  "fontSize",
  "lineHeight",
  "letterSpacing",
  "borderWidth",
  "borderRadius",
  "top",
  "right",
  "bottom",
  "left",
  "gap",
];

// CSS properties that accept color values (show color picker)
const colorProperties = ["color", "backgroundColor", "borderColor"];

// CSS properties with predefined value options (show select dropdown)
const cssValueSuggestions: Record<string, string[]> = {
  display: [
    "block",
    "flex",
    "grid",
    "inline",
    "inline-block",
    "inline-flex",
    "none",
  ],
  position: ["static", "relative", "absolute", "fixed", "sticky"],
  textAlign: ["left", "center", "right", "justify"],
  textDecoration: ["none", "underline", "line-through", "overline"],
  textTransform: ["none", "uppercase", "lowercase", "capitalize"],
  fontStyle: ["normal", "italic"],
  fontWeight: ["100", "200", "300", "400", "500", "600", "700", "800", "900"],
  backgroundSize: ["auto", "cover", "contain"],
  backgroundPosition: ["center", "top", "bottom", "left", "right"],
  backgroundRepeat: ["repeat", "no-repeat", "repeat-x", "repeat-y"],
  borderStyle: ["none", "solid", "dashed", "dotted", "double"],
  flexDirection: ["row", "column", "row-reverse", "column-reverse"],
  justifyContent: [
    "flex-start",
    "center",
    "flex-end",
    "space-between",
    "space-around",
    "space-evenly",
  ],
  alignItems: ["stretch", "flex-start", "center", "flex-end", "baseline"],
  flexWrap: ["nowrap", "wrap", "wrap-reverse"],
};

const fontSizeOptions = [
  { title: "xs", value: "10px" },
  { title: "sm", value: "12px" },
  { title: "md", value: "14px" },
  { title: "lg", value: "16px" },
  { title: "xl", value: "20px" },
  { title: "2xl", value: "24px" },
  { title: "3xl", value: "30px" },
  { title: "4xl", value: "36px" },
  { title: "5xl", value: "48px" },
  { title: "6xl", value: "60px" },
];

const fontWeightOptions = [
  { title: "Light", value: "300" },
  { title: "Regular", value: "400" },
  { title: "Medium", value: "500" },
  { title: "Semibold", value: "600" },
  { title: "Bold", value: "700" },
];

const fontFamilyOptions = [
  { title: "System UI", value: "system-ui, -apple-system, sans-serif" },
  { title: "Arial", value: "Arial, sans-serif" },
  { title: "Helvetica", value: "Helvetica, Arial, sans-serif" },
  { title: "Times", value: "Times, serif" },
  { title: "Times New Roman", value: "Times New Roman, serif" },
  { title: "Georgia", value: "Georgia, serif" },
  { title: "Courier", value: "Courier New, monospace" },
  { title: "Monaco", value: "Monaco, monospace" },
  { title: "Verdana", value: "Verdana, sans-serif" },
  { title: "Tahoma", value: "Tahoma, sans-serif" },
  { title: "Trebuchet", value: "Trebuchet MS, sans-serif" },
  { title: "Inter", value: "Inter, sans-serif" },
  { title: "Roboto", value: "Roboto, sans-serif" },
  { title: "Open Sans", value: "Open Sans, sans-serif" },
  { title: "Lato", value: "Lato, sans-serif" },
  { title: "Montserrat", value: "Montserrat, sans-serif" },
  { title: "Poppins", value: "Poppins, sans-serif" },
  { title: "Nunito", value: "Nunito, sans-serif" },
  { title: "Source Sans Pro", value: "Source Sans Pro, sans-serif" },
  { title: "Playfair Display", value: "Playfair Display, serif" },
  { title: "Merriweather", value: "Merriweather, serif" },
  { title: "Lora", value: "Lora, serif" },
  { title: "Fira Code", value: "Fira Code, monospace" },
  { title: "JetBrains Mono", value: "JetBrains Mono, monospace" },
  { title: "Source Code Pro", value: "Source Code Pro, monospace" },
];

const basicComponents: BasicComponentType[] = [
  {
    name: "Text",
    icon: Type,
    componentType: "text",
    defaultProps: {
      tagName: "p",
      text: "Enter your text here",
      x: 300,
      y: 100,
      style: {
        width: "200px",
        fontSize: "16px",
        color: "black",
        fontWeight: "bold",
        textAlign: "left",
      },
    },
  },
  {
    name: "Image",
    icon: Image,
    componentType: "image",
    defaultProps: {
      tagName: "img",
      alt: "Brand Logo",
      src: "https://cdn.prod.website-files.com/638d465c7903a622e8a1dbd1/646f94a916959dd29f26dc90_nav_brand-image.svg",
      x: 300,
      y: 100,
      style: {
        width: "auto",
        height: "150px",
        cursor: "pointer",
      },
    },
  },
  {
    name: "Button",
    icon: MousePointerClick,
    componentType: "button",
    defaultProps: {
      tagName: "a",
      text: "Click me!",
      x: 300,
      y: 100,
      style: {
        padding: "12px 48px",
        backgroundColor: "#000000",
        color: "#ffffff",
        border: "none",
        borderRadius: "8px",
        fontWeight: "500",
        fontSize: "16px",
        lineHeight: "1",
        textDecoration: "none",
        display: "inline-block",
        cursor: "pointer",
        transition: "transform 0.2s ease, boxShadow 0.2s ease",
        boxShadow: "0px 3px 10px rgba(0, 0, 0, 0.2)",
      },
    },
  },
  {
    name: "Video",
    icon: Video,
    componentType: "video",
    defaultProps: {
      tagName: "video",
      src: "https://videos.pexels.com/video-files/1722882/1722882-sd_640_360_25fps.mp4",
      autoplay: true,
      loop: true,
      muted: true,
      playsInline: true,
      controls: true,
      x: 300,
      y: 100,
      style: {
        width: "100%",
        maxWidth: "640px",
        height: "auto",
      },
    },
  },
  {
    name: "Audio",
    icon: Music,
    componentType: "audio",
    defaultProps: {
      tagName: "audio",
      src: "https://cdn.pixabay.com/audio/2024/11/28/audio_5e1a02a578.mp3",
      controls: true,
      x: 300,
      y: 100,
      style: {
        width: "100%",
        maxWidth: "640px",
      },
    },
  },
  {
    name: "Avatar",
    icon: CircleUser,
    componentType: "avatar",
    defaultProps: {
      tagName: "img",
      alt: "Avatar",
      "data-component-type": "avatar",
      src: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=face",
      x: 300,
      y: 100,
      style: {
        width: "64px",
        height: "64px",
        borderRadius: "50%",
        objectFit: "cover",
      },
    },
  },
  {
    name: "Icon",
    icon: Star,
    componentType: "icon",
    defaultProps: {
      tagName: "div",
      "data-icon-name": "star",
      x: 300,
      y: 100,
      style: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "48px",
        height: "48px",
        color: "#000000",
      },
    },
  },
];

// Factory for layout child definitions — IDs are placeholders, regenerated at drag time
const makeLayoutChild = (
  prefix: "col" | "row",
  index: number,
  flex = "1",
): LayoutChild => ({
  id: `${GENERATED_ID_PREFIX}${prefix}-${index}-placeholder`,
  tagName: "div",
  style: {
    ...(flex ? { flex } : {}),
    backgroundColor: "rgba(139, 92, 246, 0.08)",
    border: "1px dashed rgba(139, 92, 246, 0.3)",
    borderRadius: "8px",
    padding: "16px",
    minHeight: "60px",
    overflow: "hidden",
  },
  children: [],
});

const makeLayoutProps = (
  direction: "row" | "column",
  children: LayoutChild[],
  hasMinHeight = true,
): Record<string, unknown> => ({
  tagName: "div",
  x: 100,
  y: 100,
  style: {
    display: "flex",
    flexDirection: direction,
    width: "100%",
    gap: "16px",
    ...(hasMinHeight ? { minHeight: "80px" } : {}),
    cursor: "pointer",
    pointerEvents: "auto",
  },
  children,
});

const layoutComponents: BasicComponentType[] = [
  {
    name: "2 Columns",
    icon: Columns2,
    componentType: "columns-2",
    defaultProps: makeLayoutProps("row", [
      makeLayoutChild("col", 1),
      makeLayoutChild("col", 2),
    ]),
  },
  {
    name: "3 Columns",
    icon: Columns3,
    componentType: "columns-3",
    defaultProps: makeLayoutProps("row", [
      makeLayoutChild("col", 1),
      makeLayoutChild("col", 2),
      makeLayoutChild("col", 3),
    ]),
  },
  {
    name: "Sidebar Left",
    icon: PanelLeft,
    componentType: "columns-sidebar-left",
    defaultProps: makeLayoutProps("row", [
      makeLayoutChild("col", 1, "1"),
      makeLayoutChild("col", 2, "2"),
    ]),
  },
  {
    name: "Sidebar Right",
    icon: PanelRight,
    componentType: "columns-sidebar-right",
    defaultProps: makeLayoutProps("row", [
      makeLayoutChild("col", 1, "2"),
      makeLayoutChild("col", 2, "1"),
    ]),
  },
  {
    name: "2 Rows",
    icon: Rows2,
    componentType: "rows-2",
    defaultProps: makeLayoutProps(
      "column",
      [makeLayoutChild("row", 1), makeLayoutChild("row", 2)],
      false,
    ),
  },
  {
    name: "3 Rows",
    icon: Rows3,
    componentType: "rows-3",
    defaultProps: makeLayoutProps(
      "column",
      [
        makeLayoutChild("row", 1),
        makeLayoutChild("row", 2),
        makeLayoutChild("row", 3),
      ],
      false,
    ),
  },
];

const allComponents: BasicComponentType[] = [
  ...basicComponents,
  ...layoutComponents,
];

// Convert JSX attribute names to HTML equivalents for iframe preview
function jsxToHtml(jsx: string): string {
  return jsx
    .replace(/\bclassName=/g, "class=")
    .replace(/\bhtmlFor=/g, "for=")
    .replace(/\btabIndex=/g, "tabindex=");
}

// ─── Section templates ───────────────────────────────────────────────
// Framer-inspired · visually diverse · dark: variants · sm:/md:/lg: responsive
const sectionComponents: SectionComponentType[] = [
  // ── Hero ──
  {
    name: "Hero",
    icon: LayoutPanelTop,
    componentType: "section-hero-1",
    category: "sections",
    content: `<section id="hero" className="relative w-full overflow-hidden ${ST.scrollOffset} ${ST.bgLight}">
  <div className="absolute inset-0 bg-gradient-to-br from-gray-100/60 via-transparent to-gray-50/40 dark:from-gray-900/40 dark:via-transparent dark:to-gray-950/30"></div>
  <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 md:py-36 text-center">
    <p className="inline-block text-xs sm:text-sm font-semibold tracking-widest uppercase ${ST.label} mb-4 sm:mb-6">Launching Soon</p>
    <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight ${ST.heading} leading-none">Build products<br className="hidden sm:block" /> people love</h1>
    <p className="mt-5 sm:mt-6 text-base sm:text-lg md:text-xl ${ST.body} max-w-2xl mx-auto leading-relaxed">Ship faster with a visual editor that feels like magic. Design, iterate, and deploy — all without leaving the browser.</p>
    <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
      <a className="w-full sm:w-auto px-7 py-3.5 ${ST.btnPrimary} rounded-xl text-sm font-semibold shadow-lg shadow-gray-900/10 dark:shadow-white/10 transition-all" href="#">Get started free</a>
      <a className="w-full sm:w-auto px-7 py-3.5 ${ST.btnOutline} rounded-xl text-sm font-semibold transition-all" href="#">See how it works</a>
    </div>
  </div>
</section>`,
  },
  // ── Features (3-col cards) ──
  {
    name: "Features",
    icon: LayoutGrid,
    componentType: "section-features-1",
    category: "sections",
    content: `<section id="features" className="w-full py-16 sm:py-20 md:py-28 ${ST.scrollOffset} ${ST.bgMuted}">
  <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-16">
      <p className="text-sm font-semibold tracking-widest uppercase ${ST.label} mb-3">Features</p>
      <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight ${ST.heading}">Everything you need to ship</h2>
      <p className="mt-4 text-base sm:text-lg ${ST.body} leading-relaxed">A complete toolkit designed to keep you in flow — from idea to production.</p>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
      <div className="rounded-2xl ${ST.bgCard} p-6 sm:p-8 ${ST.ring} ${ST.ringHover} hover:shadow-lg transition-all">
        <div className="w-10 h-10 rounded-xl ${ST.bgPlaceholder} flex items-center justify-center mb-5"><span className="text-lg">&#9889;</span></div>
        <h3 className="text-base font-bold ${ST.heading} mb-2">Instant Preview</h3>
        <p className="text-sm ${ST.body} leading-relaxed">See every change reflected in real time. No build step, no refresh, no waiting.</p>
      </div>
      <div className="rounded-2xl ${ST.bgCard} p-6 sm:p-8 ${ST.ring} ${ST.ringHover} hover:shadow-lg transition-all">
        <div className="w-10 h-10 rounded-xl ${ST.bgPlaceholder} flex items-center justify-center mb-5"><span className="text-lg">&#127912;</span></div>
        <h3 className="text-base font-bold ${ST.heading} mb-2">Pixel-Perfect Design</h3>
        <p className="text-sm ${ST.body} leading-relaxed">Fine-tune every detail with precision controls and smart alignment guides.</p>
      </div>
      <div className="rounded-2xl ${ST.bgCard} p-6 sm:p-8 ${ST.ring} ${ST.ringHover} hover:shadow-lg transition-all sm:col-span-2 lg:col-span-1">
        <div className="w-10 h-10 rounded-xl ${ST.bgPlaceholder} flex items-center justify-center mb-5"><span className="text-lg">&#128295;</span></div>
        <h3 className="text-base font-bold ${ST.heading} mb-2">Fully Customizable</h3>
        <p className="text-sm ${ST.body} leading-relaxed">Extend with your own components, themes, and integrations. Make it yours.</p>
      </div>
    </div>
  </div>
</section>`,
  },
  // ── Split Feature (image + text) ──
  {
    name: "Split Feature",
    icon: SplitSquareHorizontal,
    componentType: "section-split-1",
    category: "sections",
    content: `<section id="about" className="w-full py-16 sm:py-20 md:py-28 ${ST.scrollOffset} ${ST.bgLight}">
  <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
      <div className="aspect-[4/3] rounded-3xl ${ST.bgPlaceholder} flex items-center justify-center order-2 lg:order-1">
        <div className="w-20 h-20 rounded-2xl ${ST.bgCard} shadow-xl"></div>
      </div>
      <div className="order-1 lg:order-2">
        <div className="inline-flex px-3 py-1 rounded-full ${ST.bgPlaceholder} text-xs font-semibold ${ST.label} mb-4">New Feature</div>
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight ${ST.heading} leading-tight">Design pages on our Canvas with world-class layout tools.</h2>
        <p className="mt-4 text-base sm:text-lg ${ST.body} leading-relaxed">Flexible grids, smart snap, and real responsive breakpoints let you build production-ready layouts — visually.</p>
        <a className="inline-flex items-center mt-6 px-5 py-2.5 ${ST.btnPrimary} rounded-lg text-sm font-semibold transition-all" href="#">Learn more</a>
      </div>
    </div>
  </div>
</section>`,
  },
  // ── Stats / Metrics ──
  {
    name: "Stats",
    icon: Award,
    componentType: "section-stats-1",
    category: "sections",
    content: `<section id="stats" className="w-full py-16 sm:py-20 md:py-24 ${ST.scrollOffset} ${ST.bgDark}">
  <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="text-center mb-10 sm:mb-14">
      <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">Trusted by thousands</h2>
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
      <div className="text-center p-6 rounded-2xl bg-white/5 ring-1 ring-white/10">
        <p className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white">30+</p>
        <p className="mt-2 text-sm text-gray-400">Awards</p>
      </div>
      <div className="text-center p-6 rounded-2xl bg-white/5 ring-1 ring-white/10">
        <p className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white">32+</p>
        <p className="mt-2 text-sm text-gray-400">Investments</p>
      </div>
      <div className="text-center p-6 rounded-2xl bg-white/5 ring-1 ring-white/10">
        <p className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white">10K</p>
        <p className="mt-2 text-sm text-gray-400">Customers</p>
      </div>
      <div className="text-center p-6 rounded-2xl bg-white/10 ring-1 ring-white/20">
        <p className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white">99%</p>
        <p className="mt-2 text-sm text-gray-400">Uptime</p>
      </div>
    </div>
  </div>
</section>`,
  },
  // ── Testimonial ──
  {
    name: "Testimonial",
    icon: MessageSquareQuote,
    componentType: "section-testimonial-1",
    category: "sections",
    content: `<section id="testimonials" className="w-full py-16 sm:py-20 md:py-28 ${ST.scrollOffset} ${ST.bgLight}">
  <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="rounded-3xl ${ST.bgMuted} p-8 sm:p-12 md:p-16">
      <div className="text-4xl sm:text-5xl ${ST.muted} font-serif leading-none mb-6">&ldquo;</div>
      <blockquote className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight ${ST.heading} leading-snug">This is one of the best tools I have ever used. It completely changed how our team works.</blockquote>
      <div className="mt-8 flex items-center gap-4">
        <div className="w-12 h-12 rounded-full ${ST.btnPrimary} flex items-center justify-center font-bold text-sm">DS</div>
        <div>
          <p className="text-sm font-semibold ${ST.heading}">Daniela Santos</p>
          <p className="text-sm ${ST.body}">Head of Design, Acme</p>
        </div>
      </div>
    </div>
  </div>
</section>`,
  },
  // ── CTA (card style) ──
  {
    name: "CTA",
    icon: Megaphone,
    componentType: "section-cta-1",
    category: "sections",
    content: `<section id="cta" className="w-full py-16 sm:py-20 md:py-28 ${ST.scrollOffset} ${ST.bgLight}">
  <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="relative overflow-hidden rounded-3xl ${ST.bgDark} px-6 sm:px-12 md:px-16 py-12 sm:py-16 md:py-20 text-center">
      <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
      <div className="relative">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight text-white leading-tight">Ready to build<br className="hidden sm:block" /> something great?</h2>
        <p className="mt-4 sm:mt-5 text-sm sm:text-base text-white/70 max-w-lg mx-auto leading-relaxed">Join 10,000+ teams who ship faster. Free to start, no credit card required.</p>
        <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
          <a className="w-full sm:w-auto px-8 py-3.5 bg-white text-gray-900 rounded-xl text-sm font-semibold shadow-lg shadow-black/20 hover:bg-gray-100 transition-all" href="#">Start for free</a>
          <a className="w-full sm:w-auto px-8 py-3.5 text-white rounded-xl text-sm font-semibold ring-1 ring-white/30 hover:ring-white/60 hover:bg-white/10 transition-all" href="#">Talk to sales</a>
        </div>
      </div>
    </div>
  </div>
</section>`,
  },
  // ── Newsletter / Stay in the loop ──
  {
    name: "Newsletter",
    icon: Mail,
    componentType: "section-newsletter-1",
    category: "sections",
    content: `<section id="newsletter" className="w-full py-16 sm:py-20 md:py-28 ${ST.scrollOffset} ${ST.bgMuted}">
  <div className="max-w-xl mx-auto px-4 sm:px-6 text-center">
    <div className="w-12 h-12 rounded-2xl ${ST.bgPlaceholder} flex items-center justify-center mx-auto mb-6"><span className="text-xl">&#9993;</span></div>
    <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight ${ST.heading}">Stay in the loop</h2>
    <p className="mt-2 text-base ${ST.body}">Get updates on new features and releases.</p>
    <div className="mt-8 flex flex-col sm:flex-row gap-3">
      <input className="flex-1 px-4 py-3 rounded-xl ${ST.bgCard} text-sm ${ST.heading} placeholder-gray-400 dark:placeholder-gray-500 ${ST.ring} focus:ring-2 outline-none transition-all" placeholder="Enter your email" />
      <a className="px-6 py-3 ${ST.btnPrimary} rounded-xl text-sm font-semibold text-center transition-all whitespace-nowrap" href="#">Subscribe</a>
    </div>
    <p className="mt-3 text-xs ${ST.muted}">No spam. Unsubscribe anytime.</p>
  </div>
</section>`,
  },
  // ── Landing Page Hero (title + subtitle + CTA + mockup) ──
  {
    name: "Landing Hero",
    icon: MonitorSmartphone,
    componentType: "section-landing-hero-1",
    category: "sections",
    content: `<section id="hero" className="w-full py-20 sm:py-28 md:py-36 ${ST.scrollOffset} ${ST.bgMuted}">
  <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
    <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-extrabold tracking-tight ${ST.heading} leading-none">Landing Page.</h1>
    <p className="mt-3 text-3xl sm:text-4xl md:text-5xl font-bold ${ST.muted} italic">Simple, bold.</p>
    <div className="mt-10 flex items-center justify-center gap-3">
      <a className="px-7 py-3 ${ST.btnPrimary} rounded-lg text-sm font-semibold transition-all" href="#">Sign Up</a>
      <a className="px-7 py-3 ${ST.btnSecondary} rounded-lg text-sm font-semibold transition-all" href="#">Download</a>
    </div>
    <div className="mt-14 sm:mt-20 aspect-[16/10] rounded-3xl ${ST.bgPlaceholder} flex items-center justify-center ring-1 ring-gray-200/60 dark:ring-gray-700/60 shadow-xl">
      <div className="flex items-end gap-2 opacity-20">
        <div className="w-16 h-16 bg-white dark:bg-gray-600"></div>
        <div className="w-16 h-16 rounded-full bg-white dark:bg-gray-600"></div>
        <div className="w-0 h-0 border-l-[32px] border-r-[32px] border-b-[56px] border-l-transparent border-r-transparent border-b-white dark:border-b-gray-600"></div>
      </div>
    </div>
  </div>
</section>`,
  },
  // ── Bento Features (asymmetric grid) ──
  {
    name: "Bento Grid",
    icon: Grid3X3,
    componentType: "section-bento-features-1",
    category: "sections",
    content: `<section id="features" className="w-full py-16 sm:py-20 md:py-28 ${ST.scrollOffset} ${ST.bgLight}">
  <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="md:col-span-2 rounded-3xl ${ST.bgDark} p-8 sm:p-10 text-white min-h-[240px] flex flex-col justify-end">
        <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Design at the speed of thought.</h3>
        <p className="mt-2 text-sm text-white/70 max-w-md">Drag, drop, and publish. No code required, no compromises made.</p>
      </div>
      <div className="rounded-3xl ${ST.bgPlaceholder} p-8 flex items-center justify-center min-h-[240px]">
        <div className="w-16 h-16 rounded-2xl ${ST.bgCard} shadow-lg"></div>
      </div>
      <div className="rounded-3xl ${ST.bgMuted} p-8 flex items-center justify-center min-h-[200px]">
        <div className="w-16 h-16 rounded-full bg-gray-200 dark:bg-gray-700"></div>
      </div>
      <div className="md:col-span-2 rounded-3xl ${ST.bgDark} p-8 sm:p-10 text-white min-h-[200px] flex flex-col justify-end">
        <p className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-2">Analytics</p>
        <h3 className="text-xl font-bold">Track everything that matters.</h3>
        <p className="mt-1 text-sm text-gray-400">Real-time insights, zero setup.</p>
      </div>
    </div>
  </div>
</section>`,
  },
  // ── 2-Column Feature Cards ──
  {
    name: "Feature Cards",
    icon: GalleryHorizontalEnd,
    componentType: "section-feature-cards-2",
    category: "sections",
    content: `<section id="features" className="w-full py-16 sm:py-20 md:py-28 ${ST.scrollOffset} ${ST.bgMuted}">
  <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="rounded-3xl ${ST.bgCard} p-6 sm:p-8 md:p-10 shadow-sm">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
        <div>
          <div className="aspect-[4/3] rounded-2xl ${ST.bgPlaceholder} flex items-center justify-center mb-5">
            <div className="w-12 h-12 ${ST.bgCard} rounded-xl shadow-lg"></div>
          </div>
          <h3 className="text-base font-bold ${ST.heading}">Feature title.</h3>
          <p className="mt-1 text-sm ${ST.body} leading-relaxed">This is a feature description spanning a couple of lines.</p>
        </div>
        <div>
          <div className="aspect-[4/3] rounded-2xl ${ST.bgPlaceholder} flex items-center justify-center mb-5">
            <div className="w-12 h-12 rounded-full ${ST.bgCard} shadow-lg"></div>
          </div>
          <h3 className="text-base font-bold ${ST.heading}">Feature title.</h3>
          <p className="mt-1 text-sm ${ST.body} leading-relaxed">This is a feature description spanning a couple of lines.</p>
        </div>
      </div>
    </div>
  </div>
</section>`,
  },
  // ── 3-Column Feature Cards with icons ──
  {
    name: "Icon Cards",
    icon: Layers,
    componentType: "section-feature-cards-3",
    category: "sections",
    content: `<section id="features" className="w-full py-16 sm:py-20 md:py-28 ${ST.scrollOffset} ${ST.bgLight}">
  <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
      <div className="rounded-3xl ${ST.bgMuted} p-6 sm:p-8 text-center">
        <div className="aspect-[4/3] rounded-2xl ${ST.bgPlaceholder} flex items-center justify-center mb-5 shadow-sm">
          <div className="w-14 h-14 rounded-2xl bg-gray-200 dark:bg-gray-700"></div>
        </div>
        <p className="text-xs font-semibold ${ST.label} uppercase tracking-wider">Design</p>
        <h3 className="mt-1 text-base font-bold ${ST.heading}">Visual Editor</h3>
      </div>
      <div className="rounded-3xl ${ST.bgMuted} p-6 sm:p-8 text-center">
        <div className="aspect-[4/3] rounded-2xl ${ST.bgPlaceholder} flex items-center justify-center mb-5 shadow-sm">
          <div className="w-14 h-14 rounded-full bg-gray-200 dark:bg-gray-700"></div>
        </div>
        <p className="text-xs font-semibold ${ST.label} uppercase tracking-wider">Deploy</p>
        <h3 className="mt-1 text-base font-bold ${ST.heading}">One-Click Ship</h3>
      </div>
      <div className="rounded-3xl ${ST.bgMuted} p-6 sm:p-8 text-center sm:col-span-2 lg:col-span-1">
        <div className="aspect-[4/3] rounded-2xl ${ST.bgPlaceholder} flex items-center justify-center mb-5 shadow-sm">
          <div className="w-0 h-0 border-l-[28px] border-r-[28px] border-b-[48px] border-l-transparent border-r-transparent border-b-gray-300 dark:border-b-gray-600"></div>
        </div>
        <p className="text-xs font-semibold ${ST.label} uppercase tracking-wider">Scale</p>
        <h3 className="mt-1 text-base font-bold ${ST.heading}">Global CDN</h3>
      </div>
    </div>
  </div>
</section>`,
  },
  // ── Pricing ──
  {
    name: "Pricing",
    icon: CreditCard,
    componentType: "section-pricing-1",
    category: "sections",
    content: `<section id="pricing" className="w-full py-16 sm:py-20 md:py-28 ${ST.scrollOffset} ${ST.bgLight}">
  <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="text-center mb-12 sm:mb-16">
      <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight ${ST.heading}">Pricing</h2>
      <p className="mt-2 text-base sm:text-lg ${ST.body}">Simple plans for every team.</p>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 items-start">
      <div className="rounded-3xl ${ST.bgCard} p-6 sm:p-8 ${ST.ring} flex flex-col">
        <p className="text-sm font-semibold ${ST.body}">Free</p>
        <p className="mt-2 text-4xl font-extrabold tracking-tight ${ST.heading}">$0</p>
        <p className="text-sm ${ST.muted}">per month</p>
        <div className="mt-6 flex flex-col gap-3 flex-1">
          <div className="flex items-center gap-2"><span className="${ST.muted}">&#10003;</span><span className="text-sm text-gray-600 dark:text-gray-300">1 project</span></div>
          <div className="flex items-center gap-2"><span className="${ST.muted}">&#10003;</span><span className="text-sm text-gray-600 dark:text-gray-300">Basic analytics</span></div>
          <div className="flex items-center gap-2"><span className="${ST.muted}">&#10003;</span><span className="text-sm text-gray-600 dark:text-gray-300">Community support</span></div>
        </div>
        <a className="mt-8 w-full py-3 rounded-xl text-sm font-semibold text-center ${ST.btnOutline} transition-all" href="#">Get started</a>
      </div>
      <div className="rounded-3xl bg-gray-900 dark:bg-white p-6 sm:p-8 flex flex-col relative ring-2 ring-gray-900 dark:ring-white">
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-xs font-semibold rounded-full">Popular</div>
        <p className="text-sm font-semibold ${ST.muted}">Pro</p>
        <p className="mt-2 text-4xl font-extrabold tracking-tight text-white dark:text-gray-900">$20</p>
        <p className="text-sm ${ST.body}">per month</p>
        <div className="mt-6 flex flex-col gap-3 flex-1">
          <div className="flex items-center gap-2"><span className="${ST.body}">&#10003;</span><span className="text-sm text-gray-300 dark:text-gray-600">Unlimited projects</span></div>
          <div className="flex items-center gap-2"><span className="${ST.body}">&#10003;</span><span className="text-sm text-gray-300 dark:text-gray-600">Advanced analytics</span></div>
          <div className="flex items-center gap-2"><span className="${ST.body}">&#10003;</span><span className="text-sm text-gray-300 dark:text-gray-600">Priority support</span></div>
        </div>
        <a className="mt-8 w-full py-3 rounded-xl text-sm font-semibold text-center ${ST.btnPrimary} transition-all shadow-lg" href="#">Get started</a>
      </div>
      <div className="rounded-3xl ${ST.bgCard} p-6 sm:p-8 ${ST.ring} flex flex-col sm:col-span-2 lg:col-span-1">
        <p className="text-sm font-semibold ${ST.body}">Team</p>
        <p className="mt-2 text-4xl font-extrabold tracking-tight ${ST.heading}">$40</p>
        <p className="text-sm ${ST.muted}">per month</p>
        <div className="mt-6 flex flex-col gap-3 flex-1">
          <div className="flex items-center gap-2"><span className="${ST.muted}">&#10003;</span><span className="text-sm text-gray-600 dark:text-gray-300">Everything in Pro</span></div>
          <div className="flex items-center gap-2"><span className="${ST.muted}">&#10003;</span><span className="text-sm text-gray-600 dark:text-gray-300">Team collaboration</span></div>
          <div className="flex items-center gap-2"><span className="${ST.muted}">&#10003;</span><span className="text-sm text-gray-600 dark:text-gray-300">Custom domain</span></div>
        </div>
        <a className="mt-8 w-full py-3 rounded-xl text-sm font-semibold text-center ${ST.btnOutline} transition-all" href="#">Get started</a>
      </div>
    </div>
  </div>
</section>`,
  },
  // ── Footer ──
  {
    name: "Footer",
    icon: PanelTopClose,
    componentType: "section-footer-1",
    category: "sections",
    content: `<footer id="footer" className="w-full ${ST.scrollOffset} ${ST.bgDark}">
  <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8 lg:gap-12">
      <div className="col-span-2 sm:col-span-3 lg:col-span-2">
        <span className="text-lg font-bold text-white tracking-tight">Brand</span>
        <p className="mt-3 text-sm ${ST.muted} leading-relaxed max-w-xs">Building tools that help teams design, ship, and scale digital experiences.</p>
      </div>
      <div>
        <h4 className="text-xs font-semibold ${ST.body} uppercase tracking-wider mb-4">Product</h4>
        <div className="flex flex-col gap-2.5">
          <a className="text-sm ${ST.muted} hover:text-white transition-colors" href="#features">Features</a>
          <a className="text-sm ${ST.muted} hover:text-white transition-colors" href="#pricing">Pricing</a>
          <a className="text-sm ${ST.muted} hover:text-white transition-colors" href="#newsletter">Newsletter</a>
        </div>
      </div>
      <div>
        <h4 className="text-xs font-semibold ${ST.body} uppercase tracking-wider mb-4">Company</h4>
        <div className="flex flex-col gap-2.5">
          <a className="text-sm ${ST.muted} hover:text-white transition-colors" href="#about">About</a>
          <a className="text-sm ${ST.muted} hover:text-white transition-colors" href="#testimonials">Testimonials</a>
          <a className="text-sm ${ST.muted} hover:text-white transition-colors" href="#stats">Stats</a>
        </div>
      </div>
      <div>
        <h4 className="text-xs font-semibold ${ST.body} uppercase tracking-wider mb-4">Legal</h4>
        <div className="flex flex-col gap-2.5">
          <a className="text-sm ${ST.muted} hover:text-white transition-colors" href="#">Privacy</a>
          <a className="text-sm ${ST.muted} hover:text-white transition-colors" href="#">Terms</a>
          <a className="text-sm ${ST.muted} hover:text-white transition-colors" href="#">Security</a>
        </div>
      </div>
    </div>
    <div className="border-t ${ST.border} mt-10 sm:mt-12 pt-6 sm:pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
      <p className="text-xs text-gray-600">© 2026 Brand. All rights reserved.</p>
      <div className="flex items-center gap-5">
        <a className="text-xs text-gray-600 hover:text-gray-400 transition-colors" href="#">Twitter</a>
        <a className="text-xs text-gray-600 hover:text-gray-400 transition-colors" href="#">GitHub</a>
        <a className="text-xs text-gray-600 hover:text-gray-400 transition-colors" href="#">Discord</a>
      </div>
    </div>
  </div>
</footer>`,
  },
];

// ─── Navigation templates ────────────────────────────────────────────
// Pre-built nav bars · dark: variants · responsive
const navigationComponents: SectionComponentType[] = [
  // ── Minimal (links only) ──
  {
    name: "Minimal",
    icon: Menu,
    componentType: "nav-minimal-1",
    category: "navigation",
    content: `<nav className="w-full px-4 sm:px-6 lg:px-8 py-4 bg-white/80 dark:bg-gray-950/80 backdrop-blur-lg border-b ${ST.border} flex items-center justify-between">
  <span className="text-base font-bold tracking-tight ${ST.heading}">Brand</span>
  <div className="flex items-center gap-1 sm:gap-1.5">
    <a className="px-3 sm:px-4 py-2 text-sm ${ST.body} hover:${ST.heading} rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" href="#features">Features</a>
    <a className="px-3 sm:px-4 py-2 text-sm ${ST.body} hover:${ST.heading} rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" href="#pricing">Pricing</a>
    <a className="px-3 sm:px-4 py-2 text-sm ${ST.body} hover:${ST.heading} rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" href="#cta">Contact</a>
  </div>
  ${NAV_SCROLL_STYLE}
</nav>`,
  },
  // ── With CTA button ──
  {
    name: "With CTA",
    icon: PanelTop,
    componentType: "nav-cta-1",
    category: "navigation",
    content: `<nav className="w-full px-4 sm:px-6 lg:px-8 py-4 bg-white/80 dark:bg-gray-950/80 backdrop-blur-lg border-b ${ST.border} flex items-center justify-between">
  <span className="text-base font-bold tracking-tight ${ST.heading}">Brand</span>
  <div className="flex items-center gap-1 sm:gap-1.5">
    <a className="px-3 sm:px-4 py-2 text-sm ${ST.body} hover:${ST.heading} rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" href="#features">Features</a>
    <a className="px-3 sm:px-4 py-2 text-sm ${ST.body} hover:${ST.heading} rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" href="#pricing">Pricing</a>
    <a className="hidden sm:inline-flex px-4 py-2 text-sm ${ST.body} hover:${ST.heading} rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" href="#about">About</a>
    <a className="ml-1 sm:ml-2 px-4 py-2 ${ST.btnPrimary} rounded-lg text-sm font-semibold transition-all" href="#hero">Get Started</a>
  </div>
  ${NAV_SCROLL_STYLE}
</nav>`,
  },
  // ── Centered links ──
  {
    name: "Centered",
    icon: AlignHorizontalJustifyCenter,
    componentType: "nav-centered-1",
    category: "navigation",
    content: `<nav className="w-full px-4 sm:px-6 lg:px-8 py-4 ${ST.bgLight} border-b ${ST.border}">
  <div className="max-w-6xl mx-auto flex items-center justify-between">
    <span className="text-base font-bold tracking-tight ${ST.heading}">Brand</span>
    <div className="hidden md:flex items-center gap-1">
      <a className="px-4 py-2 text-sm ${ST.body} hover:${ST.heading} rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" href="#features">Features</a>
      <a className="px-4 py-2 text-sm ${ST.body} hover:${ST.heading} rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" href="#about">About</a>
      <a className="px-4 py-2 text-sm ${ST.body} hover:${ST.heading} rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" href="#testimonials">Testimonials</a>
      <a className="px-4 py-2 text-sm ${ST.body} hover:${ST.heading} rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" href="#pricing">Pricing</a>
    </div>
    <a className="px-4 py-2 ${ST.btnPrimary} rounded-lg text-sm font-semibold transition-all" href="#cta">Sign Up</a>
  </div>
  ${NAV_SCROLL_STYLE}
</nav>`,
  },
  // ── Pill-shaped nav bar (floating) ──
  {
    name: "Floating Pill",
    icon: Pill,
    componentType: "nav-pill-1",
    category: "navigation",
    content: `<div className="w-full px-4 sm:px-6 pt-4 bg-transparent">
  <nav className="max-w-3xl mx-auto px-2 py-2 bg-white/90 dark:bg-gray-900/90 backdrop-blur-lg rounded-full ring-1 ring-gray-200/60 dark:ring-gray-700/60 shadow-lg shadow-gray-900/5 dark:shadow-black/20 flex items-center justify-between">
    <span className="pl-4 text-sm font-bold tracking-tight ${ST.heading}">Brand</span>
    <div className="flex items-center gap-0.5">
      <a className="px-3 sm:px-4 py-1.5 text-sm ${ST.body} hover:${ST.heading} rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" href="#features">Features</a>
      <a className="px-3 sm:px-4 py-1.5 text-sm ${ST.body} hover:${ST.heading} rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" href="#about">About</a>
      <a className="hidden sm:inline-flex px-4 py-1.5 text-sm ${ST.body} hover:${ST.heading} rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" href="#pricing">Pricing</a>
      <a className="ml-1 px-4 py-1.5 ${ST.btnPrimary} rounded-full text-sm font-semibold transition-all" href="#cta">Sign Up</a>
    </div>
    ${NAV_SCROLL_STYLE}
  </nav>
</div>`,
  },
  // ── Dropdown Nav (hover to reveal) ──
  {
    name: "Dropdown",
    icon: ChevronDown,
    componentType: "nav-dropdown-1",
    category: "navigation",
    content: `<nav className="w-full px-4 sm:px-6 lg:px-8 py-4 ${ST.bgLight} border-b ${ST.border} flex items-center justify-between">
  <span className="text-base font-bold tracking-tight ${ST.heading}">Brand</span>
  <div className="flex items-center gap-1 sm:gap-1.5">
    <div className="relative group">
      <button className="px-3 sm:px-4 py-2 text-sm ${ST.body} hover:${ST.heading} rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all inline-flex items-center gap-1">Products <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 5l3 3 3-3"></path></svg></button>
      <div className="absolute top-full left-0 mt-1 w-48 ${ST.bgLight} rounded-xl ${ST.ring} shadow-xl p-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
        <a className="block px-3 py-2 text-sm ${ST.body} hover:${ST.heading} hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all" href="#stats">Analytics</a>
        <a className="block px-3 py-2 text-sm ${ST.body} hover:${ST.heading} hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all" href="#features">Features</a>
        <a className="block px-3 py-2 text-sm ${ST.body} hover:${ST.heading} hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all" href="#about">About</a>
      </div>
    </div>
    <a className="px-3 sm:px-4 py-2 text-sm ${ST.body} hover:${ST.heading} rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" href="#pricing">Pricing</a>
    <a className="hidden sm:inline-flex px-4 py-2 text-sm ${ST.body} hover:${ST.heading} rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" href="#testimonials">Testimonials</a>
    <a className="ml-1 sm:ml-2 px-4 py-2 ${ST.btnPrimary} rounded-lg text-sm font-semibold transition-all" href="#cta">Get Started</a>
  </div>
  ${NAV_SCROLL_STYLE}
</nav>`,
  },
  // ── Split Nav (logo | centered links | CTA) ──
  {
    name: "Split",
    icon: Columns3,
    componentType: "nav-split-1",
    category: "navigation",
    content: `<nav className="w-full px-4 sm:px-6 lg:px-8 py-4 ${ST.bgLight} border-b ${ST.border}">
  <div className="max-w-6xl mx-auto grid grid-cols-3 items-center">
    <span className="text-base font-bold tracking-tight ${ST.heading}">Brand</span>
    <div className="hidden md:flex items-center justify-center gap-1">
      <a className="px-4 py-2 text-sm ${ST.body} hover:${ST.heading} rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" href="#features">Features</a>
      <a className="px-4 py-2 text-sm ${ST.body} hover:${ST.heading} rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" href="#pricing">Pricing</a>
      <a className="px-4 py-2 text-sm ${ST.body} hover:${ST.heading} rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" href="#about">About</a>
    </div>
    <div className="flex items-center justify-end gap-2">
      <a className="px-4 py-2 text-sm ${ST.body} hover:${ST.heading} rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" href="#cta">Log in</a>
      <a className="px-4 py-2 ${ST.btnPrimary} rounded-lg text-sm font-semibold transition-all" href="#cta">Sign Up</a>
    </div>
  </div>
  ${NAV_SCROLL_STYLE}
</nav>`,
  },
  // ── With Search input ──
  {
    name: "With Search",
    icon: Search,
    componentType: "nav-search-1",
    category: "navigation",
    content: `<nav className="w-full px-4 sm:px-6 lg:px-8 py-3 ${ST.bgLight} border-b ${ST.border} flex items-center justify-between gap-4">
  <span className="text-base font-bold tracking-tight ${ST.heading} shrink-0">Brand</span>
  <div className="hidden sm:flex flex-1 max-w-md mx-auto">
    <div className="w-full relative">
      <input className="w-full pl-9 pr-4 py-2 text-sm ${ST.bgMuted} ${ST.heading} rounded-lg ${ST.ring} placeholder:${ST.muted} focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600 transition-all" placeholder="Search..." type="text" />
      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${ST.muted}" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.3-4.3"></path></svg>
    </div>
  </div>
  <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
    <a className="px-3 sm:px-4 py-2 text-sm ${ST.body} hover:${ST.heading} rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" href="#features">Features</a>
    <a className="px-4 py-2 ${ST.btnPrimary} rounded-lg text-sm font-semibold transition-all" href="#cta">Sign Up</a>
  </div>
  ${NAV_SCROLL_STYLE}
</nav>`,
  },
];

// Regenerate layout children IDs so each layout instance has unique child IDs
function regenerateLayoutChildrenIds(props: Record<string, unknown>): void {
  const children = props.children;
  if (!children || !Array.isArray(children)) return;
  props.children = children.map(
    (child: Record<string, unknown>, index: number) => {
      const childId = typeof child.id === "string" ? child.id : "";
      const prefix = childId.startsWith(LAYOUT_ROW_PREFIX) ? "row" : "col";
      return {
        ...child,
        id: makeLayoutChildId(prefix, index + 1),
        children: Array.isArray(child.children) ? [...child.children] : [],
      };
    },
  );
}

function parseSourceLocation(str: string) {
  // Handle optidev-generated IDs (no file:line:col format)
  if (isGeneratedId(str)) {
    return {
      fileName: str,
      lineNumber: 0,
      columnNumber: 0,
    };
  }
  const match = str.match(/(.*):(\d+):(\d+)/);
  if (!match) return null;
  return {
    fileName: match[1],
    lineNumber: Number(match[2]),
    columnNumber: Number(match[3]),
  };
}

function normalizeDropPosition(dropPosition: string, layoutType?: string) {
  if (dropPosition === "inside") return "inside";
  if (dropPosition === "top" || dropPosition === "left") return "top";
  if (dropPosition === "bottom" || dropPosition === "right") return "bottom";
  return "after";
}

// Helper to determine action type based on element source
function getActionType(element: {
  source?: { fileName?: string };
}): "add" | "update" {
  const fileName = element.source?.fileName;
  if (!fileName || !isGeneratedId(fileName)) return "update";
  // Layout children (col/row) are part of a layout parent, not independent adds
  if (isLayoutChild(fileName)) return "update";
  // Section children (optidev-xxx-cN) are part of a section, not independent adds
  if (isSectionChild(fileName)) return "update";
  return "add";
}

export {
  stripUnit,
  stripPx,
  addPx,
  addUnit,
  parseNumber,
  cssPropertyOptions,
  parseTextShadow,
  parseBoxShadow,
  formatTextShadow,
  formatBoxShadow,
  propertiesWithPx,
  colorProperties,
  cssValueSuggestions,
  fontSizeOptions,
  fontWeightOptions,
  fontFamilyOptions,
  basicComponents,
  layoutComponents,
  allComponents,
  jsxToHtml,
  ST,
  sectionComponents,
  navigationComponents,
  regenerateLayoutChildrenIds,
  parseSourceLocation,
  normalizeDropPosition,
  getActionType,
};

// Re-export generated-id helpers so existing consumers don't need to change imports
export { isSectionChild, getSectionParentId } from "./generated-id";
